import { useEffect, useRef } from 'react'
import { Capacitor } from '@capacitor/core'
import {
  initNativeOrderAlerts,
  startOrderAlertService,
  stopOrderAlertService,
} from './nativeOrderAlerts'

type UseNativeOrderAlertRuntimeOptions = {
  /**
   * When disabled, stop foreground service only if this flag is true.
   * Useful to avoid stop/start churn during transient auth-init states.
   */
  shouldStopWhenDisabled?: boolean
}

/** Start native background order-alert runtime after staff login. */
export function useNativeOrderAlertRuntime(
  enabled: boolean,
  options?: UseNativeOrderAlertRuntimeOptions,
): void {
  const wasEnabledRef = useRef(false)
  const shouldStopWhenDisabled = options?.shouldStopWhenDisabled ?? true

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let cancelled = false

    if (enabled) {
      void (async () => {
        try {
          console.info('[order-alerts] runtime start requested')
          await initNativeOrderAlerts()
          if (!cancelled) await startOrderAlertService()
        } catch (err) {
          console.error('[order-alerts] Failed to start background runtime:', err)
        }
      })()
    } else if (shouldStopWhenDisabled && wasEnabledRef.current) {
      console.info('[order-alerts] runtime stop requested (session ended)')
      void stopOrderAlertService()
    }

    wasEnabledRef.current = enabled

    return () => {
      cancelled = true
    }
  }, [enabled, shouldStopWhenDisabled])
}
